/**
 * 提及资源模型能力校验工具
 *
 * 提供 getMentionResourceViolation() 判断单个资源是否超出当前模型能力限制。
 * 弹窗禁选和提交前兜底校验共用同一套规则，避免规则分叉。
 */
import type { MentionResource } from '@/types/mention';
import type { AIModel } from '@/types/ai';
import type { MentionToken, ReferenceImage } from '@/types/conversation-input';
import { inspectImageCompression } from '@/lib/image-compression';
import { isKnownMediaFormat, normalizeMediaFormat } from '@/lib/conversations/media-format';
import { normalizeInputReferenceLimits } from './input-reference-limits';
import {
  isVideoMentionToken,
  resolveMentionResourceVideoIdentityKey,
  resolveMentionTokenVideoIdentityKey,
  resolveVideoReferenceIdentityKey,
} from './video-extend-reference-budget';

const MB = 1024 * 1024;

export interface MentionResourceImageConstraints {
  maxSizeMb?: number;
  minDimension?: number;
  maxDimension?: number;
  minPixels?: number;
  maxPixels?: number;
  maxDimensionPx?: number;
  aspectRatioMin?: number;
  aspectRatioMax?: number;
  formats?: string[];
}

export interface MentionResourceVideoConstraints {
  maxSizeMb?: number;
  minDurationS?: number;
  maxDurationS?: number;
  totalMaxDurationS?: number;
  minDimension?: number;
  maxDimension?: number;
  minPixels?: number;
  maxPixels?: number;
  aspectRatioMin?: number;
  aspectRatioMax?: number;
  fpsMin?: number;
  fpsMax?: number;
  formats?: string[];
}

export interface MentionResourceAudioConstraints {
  maxSizeMb?: number;
  minDurationS?: number;
  maxDurationS?: number;
  totalMaxDurationS?: number;
  formats?: string[];
}

export interface MentionResourceConstraints {
  image?: MentionResourceImageConstraints;
  video?: MentionResourceVideoConstraints;
  audio?: MentionResourceAudioConstraints;
}

export type ViolationCode =
  | 'size'
  | 'dimension'
  | 'pixels'
  | 'aspect_ratio'
  | 'format'
  | 'duration'
  | 'min_duration'
  | 'total_duration'
  | 'fps';

export interface MentionResourceViolation {
  code: ViolationCode;
  message: string;
}

export interface MentionResourceCompressionNotice {
  title: string;
  description: string;
  message: string;
}

function isAudioResource(resource: MentionResource): boolean {
  return (
    resource.mimeType?.startsWith('audio/') ||
    resource.type === 'sound' ||
    resource.type === 'voice'
  );
}

function isVideoResource(resource: MentionResource): boolean {
  return resource.mimeType?.startsWith('video/') || resource.type === 'video';
}

function isVideoReferenceImage(image: ReferenceImage): boolean {
  return image.kind === 'video' || Boolean(image.mimeType?.startsWith('video/'));
}

function isAudioReferenceImage(image: ReferenceImage): boolean {
  return image.kind === 'audio' || Boolean(image.mimeType?.startsWith('audio/'));
}

function isAudioMentionToken(token: MentionToken, referenceImages: ReferenceImage[]): boolean {
  if (token.kind === 'resource' && token.resource) {
    return isAudioResource(token.resource);
  }
  if (token.kind !== 'reference-image') return false;
  const image = referenceImages.find((item) => item.id === token.referenceImageId);
  return (
    token.referenceImageKind === 'audio' ||
    image?.kind === 'audio' ||
    Boolean(image?.mimeType?.startsWith('audio/')) ||
    Boolean(token.referenceImageMimeType?.startsWith('audio/'))
  );
}

function resolveMentionTokenMediaDuration(
  token: MentionToken,
  referenceImages: ReferenceImage[]
): number | null {
  if (token.kind === 'resource' && token.resource?.durationSeconds != null) {
    return token.resource.durationSeconds;
  }
  if (token.kind === 'reference-image') {
    const image = referenceImages.find((item) => item.id === token.referenceImageId);
    if (image?.durationSeconds != null) return image.durationSeconds;
  }
  return null;
}

function resolveMentionTokenVideoDuration(
  token: MentionToken,
  referenceImages: ReferenceImage[]
): number | null {
  if (!isVideoMentionToken(token, referenceImages)) return null;
  return resolveMentionTokenMediaDuration(token, referenceImages);
}

function resolveMentionResourceAudioIdentityKey(
  resource: Pick<MentionResource, 'submitResourceId' | 'resourceId' | 'resourceUrl'>
): string | null {
  return resolveMentionResourceVideoIdentityKey(resource);
}

function resolveMentionTokenAudioIdentityKey(
  token: MentionToken,
  referenceImages: ReferenceImage[]
): string | null {
  if (!isAudioMentionToken(token, referenceImages)) return null;
  if (token.kind === 'resource' && token.resource) {
    return resolveMentionResourceAudioIdentityKey(token.resource);
  }
  const image = referenceImages.find((item) => item.id === token.referenceImageId);
  return resolveVideoReferenceIdentityKey({
    resourceId: image?.resourceId,
    url: image?.remoteUrl || image?.url || token.referenceImageUrl,
  });
}

function hasBackendResolvableIdentity(resource: MentionResource): boolean {
  return Boolean(resource.submitResourceId || resource.resourceId);
}

function resolveMentionResourceType(image: ReferenceImage): MentionResource['type'] {
  if (image.mimeType?.startsWith('video/') || image.kind === 'video') return 'video';
  if (image.mimeType?.startsWith('audio/') || image.kind === 'audio') return 'sound';
  return 'footage';
}

function buildMediaFixHint(kind: '视频' | '音频', code: 'size' | 'duration' | 'format'): string {
  if (code === 'format') return `请先转码为当前模型支持的格式后重新引用`;
  if (kind === '视频') return '请先剪辑、裁剪或转码后重新引用';
  return '请先剪辑音频时长或转码后重新引用';
}

function buildDurationMissingViolation(kind: '视频' | '音频'): MentionResourceViolation {
  return {
    code: 'duration',
    message: `无法校验时长：${kind}缺少时长信息，请重新上传或先剪辑后重新引用`,
  };
}

function normalizeAllowedFormats(
  formats: string[] | undefined,
  kind: 'image' | 'video' | 'audio'
): Set<string> {
  return new Set(
    (formats ?? [])
      .map((format) => normalizeMediaFormat(`${kind}/${format}`))
      .filter((format) => format.length > 0)
  );
}

function resolveMentionResourceFormat(
  resource: MentionResource,
  allowedFormats: Set<string>
): string {
  const explicitFormat = resource.format ? normalizeMediaFormat(resource.format) : '';
  if (explicitFormat) return explicitFormat;

  const mimeFormat = resource.mimeType ? normalizeMediaFormat(resource.mimeType) : '';
  // 视频以 mimeType/format 为准，忽略封面扩展名（cover.jpg 不应解析成 JPEG）。
  const isVideo =
    resource.type === 'video' || Boolean(resource.mimeType?.toLowerCase().startsWith('video/'));
  if (isVideo && mimeFormat) {
    return mimeFormat;
  }

  const urlFormat = resource.resourceUrl ? normalizeMediaFormat(resource.resourceUrl) : '';
  if (urlFormat && (isKnownMediaFormat(urlFormat) || allowedFormats.has(urlFormat))) {
    return urlFormat;
  }

  return mimeFormat;
}

function formatSizeMb(fileSize: number): string {
  return `${(fileSize / MB).toFixed(1)}MB`;
}

function formatCompressionReason(reason: 'size' | 'pixels' | 'dimension'): string {
  switch (reason) {
    case 'size':
      return '文件大小';
    case 'pixels':
      return '像素总量';
    case 'dimension':
      return '最大边长';
    default: {
      const exhaustive: never = reason;
      return exhaustive;
    }
  }
}

export function referenceImageToMentionResource(image: ReferenceImage): MentionResource {
  return {
    type: resolveMentionResourceType(image),
    elementId: image.attachmentId || image.resourceId || image.id,
    elementName: '参考素材',
    resourceId: image.resourceId || image.attachmentId || image.id,
    resourceUrl: image.remoteUrl || image.url,
    thumbnailUrl: image.url,
    mimeType: image.mimeType,
    submitResourceId: image.resourceId || image.attachmentId || image.id,
    submitResourceType: image.resourceType,
    fileSize: image.fileSize,
    width: image.width ?? undefined,
    height: image.height ?? undefined,
    durationSeconds: image.durationSeconds,
    frameRate: image.frameRate ?? undefined,
    format: image.format,
  };
}

export function getReferenceAssetViolation(
  image: ReferenceImage,
  constraints: MentionResourceConstraints
): MentionResourceViolation | null {
  return getMentionResourceViolation(referenceImageToMentionResource(image), constraints);
}

export function getReferenceAssetCompressionNotice(
  image: ReferenceImage,
  constraints: MentionResourceConstraints
): MentionResourceCompressionNotice | null {
  return getMentionResourceCompressionNotice(referenceImageToMentionResource(image), constraints);
}

/** 已 @ 提及视频的最长时长；未提及的上传板视频不计入。 */
export function getMaxMentionedVideoDuration(params: {
  tokens: MentionToken[];
  referenceImages: ReferenceImage[];
}): number | null {
  let maxDuration: number | null = null;
  const seen = new Set<string>();
  for (const token of params.tokens) {
    if (!isVideoMentionToken(token, params.referenceImages)) continue;
    const duration = resolveMentionTokenVideoDuration(token, params.referenceImages);
    if (duration == null) continue;
    const key =
      resolveMentionTokenVideoIdentityKey(token, params.referenceImages) ?? `token:${token.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    if (maxDuration == null || duration > maxDuration) maxDuration = duration;
  }
  return maxDuration;
}

function collectUniqueMediaDurationsByKey(params: {
  tokens: MentionToken[];
  referenceImages: ReferenceImage[];
  excludeTokenId?: string;
  isToken: (token: MentionToken, images: ReferenceImage[]) => boolean;
  isImage: (image: ReferenceImage) => boolean;
  resolveTokenKey: (token: MentionToken, images: ReferenceImage[]) => string | null;
}): Map<string, number> {
  const durations = new Map<string, number>();
  const coveredImageIds = new Set<string>();
  const excludedImageIds = new Set<string>();

  for (const token of params.tokens) {
    if (token.kind === 'reference-image' && token.referenceImageId) {
      if (params.excludeTokenId && token.id === params.excludeTokenId) {
        excludedImageIds.add(token.referenceImageId);
      } else {
        coveredImageIds.add(token.referenceImageId);
      }
    }
    if (params.excludeTokenId && token.id === params.excludeTokenId) continue;
    if (!params.isToken(token, params.referenceImages)) continue;
    const duration = resolveMentionTokenMediaDuration(token, params.referenceImages);
    if (duration == null) continue;
    const imageId = token.kind === 'reference-image' ? token.referenceImageId : undefined;
    const key =
      params.resolveTokenKey(token, params.referenceImages) ??
      (imageId ? `image:${imageId}` : `token:${token.id}`);
    if (!durations.has(key)) durations.set(key, duration);
  }

  for (const image of params.referenceImages) {
    if (excludedImageIds.has(image.id) || coveredImageIds.has(image.id)) continue;
    if (!params.isImage(image) || image.durationSeconds == null) continue;
    const key =
      resolveVideoReferenceIdentityKey({
        resourceId: image.resourceId,
        url: image.remoteUrl || image.url,
      }) ?? `image:${image.id}`;
    if (!durations.has(key)) durations.set(key, image.durationSeconds);
  }

  return durations;
}

export function collectUniqueVideoDurationsByKey(params: {
  tokens: MentionToken[];
  referenceImages: ReferenceImage[];
  excludeTokenId?: string;
}): Map<string, number> {
  return collectUniqueMediaDurationsByKey({
    ...params,
    isToken: isVideoMentionToken,
    isImage: isVideoReferenceImage,
    resolveTokenKey: resolveMentionTokenVideoIdentityKey,
  });
}

export function collectUniqueAudioDurationsByKey(params: {
  tokens: MentionToken[];
  referenceImages: ReferenceImage[];
  excludeTokenId?: string;
}): Map<string, number> {
  return collectUniqueMediaDurationsByKey({
    ...params,
    isToken: isAudioMentionToken,
    isImage: isAudioReferenceImage,
    resolveTokenKey: resolveMentionTokenAudioIdentityKey,
  });
}

export function collectUniqueMentionDurationsByKind(params: {
  tokens: MentionToken[];
  referenceImages: ReferenceImage[];
  excludeTokenId?: string;
}): { video: Map<string, number>; audio: Map<string, number> } {
  return {
    video: collectUniqueVideoDurationsByKey(params),
    audio: collectUniqueAudioDurationsByKey(params),
  };
}

function getMentionMediaTotalDurationViolation(params: {
  kind: '视频' | '音频';
  totalMaxDurationS?: number;
  existingDurationsByKey: ReadonlyMap<string, number>;
  nextKey?: string | null;
  nextDurationSeconds?: number | null;
}): MentionResourceViolation | null {
  const totalMax = params.totalMaxDurationS;
  if (totalMax == null) return null;

  const durations = new Map(params.existingDurationsByKey);
  const hasNext = params.nextKey !== undefined || params.nextDurationSeconds !== undefined;
  if (hasNext) {
    if (params.nextDurationSeconds == null) return null;
    const nextKey = params.nextKey ?? `__anon:${params.nextDurationSeconds}`;
    // 已按身份计入：再选同一条不增加合计，弹层保持勾选可取消
    if (durations.has(nextKey)) return null;
    durations.set(nextKey, params.nextDurationSeconds);
  }

  let sum = 0;
  for (const duration of durations.values()) {
    sum += duration;
  }
  if (sum <= totalMax) return null;

  return {
    code: 'total_duration',
    message: `参考${params.kind}合计时长 ${sum.toFixed(1)}s 超过 ${totalMax}s 限制，${buildMediaFixHint(params.kind, 'duration')}`,
  };
}

export function getMentionVideoTotalDurationViolation(params: {
  totalMaxDurationS?: number;
  existingDurationsByKey: ReadonlyMap<string, number>;
  nextKey?: string | null;
  nextDurationSeconds?: number | null;
}): MentionResourceViolation | null {
  return getMentionMediaTotalDurationViolation({ kind: '视频', ...params });
}

export function getMentionAudioTotalDurationViolation(params: {
  totalMaxDurationS?: number;
  existingDurationsByKey: ReadonlyMap<string, number>;
  nextKey?: string | null;
  nextDurationSeconds?: number | null;
}): MentionResourceViolation | null {
  return getMentionMediaTotalDurationViolation({ kind: '音频', ...params });
}

export function getMentionResourceInsertViolation(params: {
  resource: MentionResource;
  constraints: MentionResourceConstraints;
  existingDurationsByKey: ReadonlyMap<string, number>;
  existingAudioDurationsByKey?: ReadonlyMap<string, number>;
}): MentionResourceViolation | null {
  const perItem = getMentionResourceViolation(params.resource, params.constraints);
  if (perItem) return perItem;
  if (isVideoResource(params.resource)) {
    return getMentionVideoTotalDurationViolation({
      totalMaxDurationS: params.constraints.video?.totalMaxDurationS,
      existingDurationsByKey: params.existingDurationsByKey,
      nextKey: resolveMentionResourceVideoIdentityKey(params.resource),
      nextDurationSeconds: params.resource.durationSeconds ?? null,
    });
  }
  if (isAudioResource(params.resource)) {
    return getMentionAudioTotalDurationViolation({
      totalMaxDurationS: params.constraints.audio?.totalMaxDurationS,
      existingDurationsByKey: params.existingAudioDurationsByKey ?? new Map(),
      nextKey: resolveMentionResourceAudioIdentityKey(params.resource),
      nextDurationSeconds: params.resource.durationSeconds ?? null,
    });
  }
  return null;
}

export function getReferenceAssetInsertViolation(params: {
  image: ReferenceImage;
  constraints: MentionResourceConstraints;
  existingDurationsByKey: ReadonlyMap<string, number>;
  existingAudioDurationsByKey?: ReadonlyMap<string, number>;
}): MentionResourceViolation | null {
  return getMentionResourceInsertViolation({
    resource: referenceImageToMentionResource(params.image),
    constraints: params.constraints,
    existingDurationsByKey: params.existingDurationsByKey,
    existingAudioDurationsByKey: params.existingAudioDurationsByKey,
  });
}

function mentionConstraintSignature(constraints?: MentionResourceConstraints): string {
  if (!constraints) return '';
  return JSON.stringify({
    image: constraints.image ?? null,
    video: constraints.video ?? null,
    audio: constraints.audio ?? null,
  });
}

export function shouldRecheckInsertedMentions(params: {
  prevModelName?: string;
  nextModelName?: string;
  prevConstraints?: MentionResourceConstraints;
  nextConstraints?: MentionResourceConstraints;
}): boolean {
  if (!params.nextConstraints) return false;
  const modelChanged = Boolean(
    params.prevModelName && params.nextModelName && params.prevModelName !== params.nextModelName
  );
  const constraintsChanged =
    params.prevConstraints !== undefined &&
    mentionConstraintSignature(params.prevConstraints) !==
      mentionConstraintSignature(params.nextConstraints);
  return modelChanged || constraintsChanged;
}

export function resolveMentionInsertViolation(params: {
  resource?: MentionResource | null;
  image?: ReferenceImage | null;
  constraints?: MentionResourceConstraints;
  tokens: MentionToken[];
  referenceImages: ReferenceImage[];
  excludeTokenId?: string;
}): MentionResourceViolation | null {
  if (!params.constraints) return null;
  const { video: existingDurationsByKey, audio: existingAudioDurationsByKey } =
    collectUniqueMentionDurationsByKind({
      tokens: params.tokens,
      referenceImages: params.referenceImages,
      excludeTokenId: params.excludeTokenId,
    });
  if (params.resource) {
    return getMentionResourceInsertViolation({
      resource: params.resource,
      constraints: params.constraints,
      existingDurationsByKey,
      existingAudioDurationsByKey,
    });
  }
  if (params.image) {
    return getReferenceAssetInsertViolation({
      image: params.image,
      constraints: params.constraints,
      existingDurationsByKey,
      existingAudioDurationsByKey,
    });
  }
  return null;
}

export function buildCompressionNoticeSummary(
  notices: MentionResourceCompressionNotice[]
): MentionResourceCompressionNotice | null {
  if (notices.length === 0) return null;
  if (notices.length === 1) return notices[0];

  return {
    title: `${notices.length} 张参考图已超过模型上限`,
    description: `提交时系统将自动压缩 ${notices.length} 张参考图。如需自定义参数，可在预览区使用编辑图片手动调整。${notices[0].description}`,
    message: `${notices.length} 张参考图已超过模型上限，提交时将自动压缩`,
  };
}

/**
 * 校验单个提及资源是否超出模型能力限制。
 * 返回 null 表示合规，否则返回第一个违规原因。
 */
export function getMentionResourceViolation(
  resource: MentionResource,
  constraints: MentionResourceConstraints
): MentionResourceViolation | null {
  const isAudio = isAudioResource(resource);
  const isVideo = isVideoResource(resource);

  if (isAudio) {
    const ac = constraints.audio;
    if (!ac) return null;

    if (ac.maxSizeMb && resource.fileSize && resource.fileSize > ac.maxSizeMb * MB) {
      return {
        code: 'size',
        message: `音频文件大小 ${formatSizeMb(resource.fileSize)} 超过 ${ac.maxSizeMb}MB 限制，${buildMediaFixHint('音频', 'size')}`,
      };
    }
    if (
      (ac.minDurationS || ac.maxDurationS) &&
      resource.durationSeconds == null &&
      !hasBackendResolvableIdentity(resource)
    ) {
      return buildDurationMissingViolation('音频');
    }
    if (
      ac.minDurationS &&
      resource.durationSeconds != null &&
      resource.durationSeconds < ac.minDurationS
    ) {
      return {
        code: 'min_duration',
        message: `音频时长 ${resource.durationSeconds.toFixed(1)}s 低于 ${ac.minDurationS}s 限制，${buildMediaFixHint('音频', 'duration')}`,
      };
    }
    if (
      ac.maxDurationS &&
      resource.durationSeconds != null &&
      resource.durationSeconds > ac.maxDurationS
    ) {
      return {
        code: 'duration',
        message: `音频时长 ${resource.durationSeconds.toFixed(1)}s 超过 ${ac.maxDurationS}s 限制，${buildMediaFixHint('音频', 'duration')}`,
      };
    }
    if (ac.formats && ac.formats.length > 0) {
      const allowed = normalizeAllowedFormats(ac.formats, 'audio');
      const fmt = resolveMentionResourceFormat(resource, allowed);
      if (fmt && !allowed.has(fmt)) {
        return {
          code: 'format',
          message: `音频格式 ${fmt.toUpperCase()} 不在当前模型支持列表内，${buildMediaFixHint('音频', 'format')}`,
        };
      }
    }
  } else if (isVideo) {
    const vc = constraints.video;
    if (!vc) return null;

    if (vc.maxSizeMb && resource.fileSize && resource.fileSize > vc.maxSizeMb * MB) {
      return {
        code: 'size',
        message: `视频文件大小 ${formatSizeMb(resource.fileSize)} 超过 ${vc.maxSizeMb}MB 限制，${buildMediaFixHint('视频', 'size')}`,
      };
    }
    if (
      (vc.minDurationS || vc.maxDurationS) &&
      resource.durationSeconds == null &&
      !hasBackendResolvableIdentity(resource)
    ) {
      return buildDurationMissingViolation('视频');
    }
    if (
      vc.minDurationS != null &&
      resource.durationSeconds != null &&
      resource.durationSeconds < vc.minDurationS
    ) {
      return {
        code: 'min_duration',
        message: `视频时长 ${resource.durationSeconds.toFixed(1)}s 低于 ${vc.minDurationS}s 限制，${buildMediaFixHint('视频', 'duration')}`,
      };
    }
    if (
      vc.maxDurationS != null &&
      resource.durationSeconds != null &&
      resource.durationSeconds > vc.maxDurationS
    ) {
      return {
        code: 'duration',
        message: `视频时长 ${resource.durationSeconds.toFixed(1)}s 超过 ${vc.maxDurationS}s 限制，${buildMediaFixHint('视频', 'duration')}`,
      };
    }
    if (vc.minDimension != null && resource.width != null && resource.height != null) {
      const minSide = Math.min(resource.width, resource.height);
      if (minSide < vc.minDimension) {
        return {
          code: 'dimension',
          message: `视频最小边 ${minSide}px 不满足 ${vc.minDimension}px 要求，${buildMediaFixHint('视频', 'duration')}`,
        };
      }
    }
    if (vc.maxDimension != null && resource.width != null && resource.height != null) {
      const maxSide = Math.max(resource.width, resource.height);
      if (maxSide > vc.maxDimension) {
        return {
          code: 'dimension',
          message: `视频最大边 ${maxSide}px 超过 ${vc.maxDimension}px 限制，${buildMediaFixHint('视频', 'duration')}`,
        };
      }
    }
    if (resource.width != null && resource.height != null) {
      const pixels = resource.width * resource.height;
      if (vc.minPixels != null && pixels < vc.minPixels) {
        return {
          code: 'pixels',
          message: `视频像素 ${(pixels / 1_000_000).toFixed(1)}M 低于 ${(vc.minPixels / 1_000_000).toFixed(1)}M 要求，${buildMediaFixHint('视频', 'duration')}`,
        };
      }
      if (vc.maxPixels != null && pixels > vc.maxPixels) {
        return {
          code: 'pixels',
          message: `视频像素 ${(pixels / 1_000_000).toFixed(1)}M 超过 ${(vc.maxPixels / 1_000_000).toFixed(1)}M 限制，${buildMediaFixHint('视频', 'duration')}`,
        };
      }
    }
    if (
      vc.aspectRatioMin != null &&
      resource.width != null &&
      resource.height != null &&
      resource.height > 0
    ) {
      const ratio = resource.width / resource.height;
      if (ratio < vc.aspectRatioMin) {
        return {
          code: 'aspect_ratio',
          message: `视频宽高比 ${ratio.toFixed(2)} 低于最小值 ${vc.aspectRatioMin.toFixed(2)}，${buildMediaFixHint('视频', 'duration')}`,
        };
      }
    }
    if (
      vc.aspectRatioMax != null &&
      resource.width != null &&
      resource.height != null &&
      resource.height > 0
    ) {
      const ratio = resource.width / resource.height;
      if (ratio > vc.aspectRatioMax) {
        return {
          code: 'aspect_ratio',
          message: `视频宽高比 ${ratio.toFixed(2)} 超过最大值 ${vc.aspectRatioMax.toFixed(2)}，${buildMediaFixHint('视频', 'duration')}`,
        };
      }
    }
    if (vc.fpsMin != null && resource.frameRate != null && resource.frameRate < vc.fpsMin) {
      return {
        code: 'fps',
        message: `视频帧率 ${resource.frameRate}fps 低于 ${vc.fpsMin}fps 要求，${buildMediaFixHint('视频', 'duration')}`,
      };
    }
    if (vc.fpsMax != null && resource.frameRate != null && resource.frameRate > vc.fpsMax) {
      return {
        code: 'fps',
        message: `视频帧率 ${resource.frameRate}fps 超过 ${vc.fpsMax}fps 限制，${buildMediaFixHint('视频', 'duration')}`,
      };
    }
    if (vc.formats && vc.formats.length > 0) {
      const allowed = normalizeAllowedFormats(vc.formats, 'video');
      const fmt = resolveMentionResourceFormat(resource, allowed);
      if (fmt && !allowed.has(fmt)) {
        return {
          code: 'format',
          message: `视频格式 ${fmt.toUpperCase()} 不在当前模型支持列表内，${buildMediaFixHint('视频', 'format')}`,
        };
      }
    }
  } else {
    const ic = constraints.image;
    if (!ic) return null;

    if (ic.minDimension != null && resource.width != null && resource.height != null) {
      const minSide = Math.min(resource.width, resource.height);
      if (minSide < ic.minDimension) {
        return {
          code: 'dimension',
          message: `图片最小边 ${minSide}px 不满足 ${ic.minDimension}px 要求`,
        };
      }
    }
    if (
      ic.aspectRatioMin != null &&
      resource.width != null &&
      resource.height != null &&
      resource.height > 0
    ) {
      const ratio = resource.width / resource.height;
      if (ratio < ic.aspectRatioMin) {
        return {
          code: 'aspect_ratio',
          message: `图片宽高比 ${ratio.toFixed(2)} 低于最小值 ${ic.aspectRatioMin.toFixed(2)}`,
        };
      }
    }
    if (
      ic.aspectRatioMax != null &&
      resource.width != null &&
      resource.height != null &&
      resource.height > 0
    ) {
      const ratio = resource.width / resource.height;
      if (ratio > ic.aspectRatioMax) {
        return {
          code: 'aspect_ratio',
          message: `图片宽高比 ${ratio.toFixed(2)} 超过最大值 ${ic.aspectRatioMax.toFixed(2)}`,
        };
      }
    }
    if (ic.formats && ic.formats.length > 0) {
      const allowed = normalizeAllowedFormats(ic.formats, 'image');
      const fmt = resolveMentionResourceFormat(resource, allowed);
      if (fmt && !allowed.has(fmt)) {
        return {
          code: 'format',
          message: `格式 ${fmt.toUpperCase()} 不在当前模型支持列表内`,
        };
      }
    }
  }

  return null;
}

/**
 * 图片大小/像素/最大边长超限可由后端自动压缩修复，因此这里只返回非阻断提示。
 */
export function getMentionResourceCompressionNotice(
  resource: MentionResource,
  constraints: MentionResourceConstraints
): MentionResourceCompressionNotice | null {
  if (isAudioResource(resource) || isVideoResource(resource)) return null;

  const ic = constraints.image;
  if (!ic) return null;

  if (resource.fileSize == null || resource.width == null || resource.height == null) return null;

  const decision = inspectImageCompression(
    {
      width: resource.width,
      height: resource.height,
      fileSize: resource.fileSize,
      mimeType: resource.mimeType || 'application/octet-stream',
    },
    {
      maxSizeMb: ic.maxSizeMb,
      maxPixels: ic.maxPixels,
      maxDimensionPx: ic.maxDimensionPx,
    }
  );

  if (!decision.needsCompression) return null;

  const reasons = decision.reasons.map(formatCompressionReason).join('、') || '图片尺寸';
  const targetDescription = decision.target.needsResize
    ? `，将从 ${resource.width}×${resource.height} 压缩到约 ${decision.target.width}×${decision.target.height}`
    : '';
  const identityDescription = hasBackendResolvableIdentity(resource)
    ? '提交时将自动压缩'
    : '提交时将尝试自动压缩';
  const description = `参考图已超过当前模型上限（${reasons}）${targetDescription}。${identityDescription}。如需自定义参数，可在预览区使用编辑图片手动调整。`;

  return {
    title: '参考图已超过模型上限',
    description,
    message: description,
  };
}

/**
 * 从 AIModel 能力配置中提取提及资源校验约束。
 * 传入 mode 时按 capabilities.byMode 解析（与后端白名单一致）。
 * 如果模型未定义某字段，对应约束为 undefined，校验时跳过该项。
 */
export function buildMentionResourceConstraints(
  model?: AIModel,
  mode?: string | null
): MentionResourceConstraints {
  const limits = normalizeInputReferenceLimits(model, mode);
  const imageRef = limits.image;
  const videoRef = limits.video;
  const audioRef = limits.audio;

  const image: MentionResourceImageConstraints = {};
  if (imageRef?.maxSizeMb) image.maxSizeMb = imageRef.maxSizeMb;
  if (imageRef?.minDimension) image.minDimension = imageRef.minDimension;
  if (imageRef?.maxPixels) image.maxPixels = imageRef.maxPixels;
  if (imageRef?.maxDimensionPx) image.maxDimensionPx = imageRef.maxDimensionPx;
  if (imageRef?.aspectRatioMin != null) image.aspectRatioMin = imageRef.aspectRatioMin;
  if (imageRef?.aspectRatioMax != null) image.aspectRatioMax = imageRef.aspectRatioMax;
  if (imageRef?.formats?.length) image.formats = imageRef.formats;

  const video: MentionResourceVideoConstraints = {};
  if (videoRef?.maxSizeMb) video.maxSizeMb = videoRef.maxSizeMb;
  if (videoRef?.minDurationS) video.minDurationS = videoRef.minDurationS;
  if (videoRef?.maxDurationS != null) video.maxDurationS = videoRef.maxDurationS;
  if (videoRef?.totalMaxDurationS) video.totalMaxDurationS = videoRef.totalMaxDurationS;
  if (videoRef?.minDimension) video.minDimension = videoRef.minDimension;
  if (videoRef?.maxDimension) video.maxDimension = videoRef.maxDimension;
  if (videoRef?.minPixels) video.minPixels = videoRef.minPixels;
  if (videoRef?.maxPixels) video.maxPixels = videoRef.maxPixels;
  if (videoRef?.aspectRatioMin != null) video.aspectRatioMin = videoRef.aspectRatioMin;
  if (videoRef?.aspectRatioMax != null) video.aspectRatioMax = videoRef.aspectRatioMax;
  if (videoRef?.fpsMin) video.fpsMin = videoRef.fpsMin;
  if (videoRef?.fpsMax) video.fpsMax = videoRef.fpsMax;
  if (videoRef?.formats?.length) video.formats = videoRef.formats;

  const audio: MentionResourceAudioConstraints = {};
  if (audioRef?.maxSizeMb) audio.maxSizeMb = audioRef.maxSizeMb;
  if (audioRef?.minDurationS) audio.minDurationS = audioRef.minDurationS;
  if (audioRef?.maxDurationS) audio.maxDurationS = audioRef.maxDurationS;
  if (audioRef?.totalMaxDurationS) audio.totalMaxDurationS = audioRef.totalMaxDurationS;
  if (audioRef?.formats?.length) audio.formats = audioRef.formats;

  return { image, video, audio };
}
